import {
    FC, ReactPortal, useRef, useLayoutEffect,
} from "react";
import ReactDOM from "react-dom";

import { isBrowser } from "@montana/helpers/browser.helper";
import { useForceUpdate } from "../force-update";

interface IPortalProps {
    appendTo?: string;
    className?: string;
}

export const Portal: FC<IPortalProps> = ({ children, className = "", appendTo = "" }: any): ReactPortal | null => {

    function getPortalRoot(): Element | undefined {
        if (isBrowser) {
            return appendTo ? document.querySelector(appendTo) : document.body;
        }

        return undefined;
    }

    const portalRoot = useRef<Element | undefined>(getPortalRoot());
    const el = useRef<HTMLElement | undefined>();

    const forceUpdate = useForceUpdate();

    function addPortalElement(): HTMLElement | undefined {
        if (isBrowser) {
            return document.createElement("div");
        }

        return undefined;
    }

    const useDynamicEffect = isBrowser ? useLayoutEffect : (): void => {/* Do nothing. */};

    useDynamicEffect((): () => void => {
        // Set here to avoid warning with portal not being SSR'ed - https://github.com/facebook/react/issues/13097
        el.current = addPortalElement();

        if (!portalRoot.current || !el.current) {
            // @ts-ignore
            return;
        }

        className.split(" ")
            .filter((c: string) => !!c)
            .map((c: string) => el.current!.classList.add(c));

        portalRoot.current.appendChild(el.current);

        forceUpdate();

        // eslint-disable-next-line consistent-return
        return (): void => {
            if (portalRoot.current && el.current) {
                portalRoot.current.removeChild(el.current);
            }
        };
    }, []);

    return (
        el.current
            ? ReactDOM.createPortal(
                children,
                el.current,
            )
            : null
    );
};
